import React, { useState, useEffect } from "react";
import { Save, Search, Loader2, Clock, MapPin, X } from "lucide-react";
import api from "../api/axiosInstance";

const InteractiveAttendanceRegister = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7),
  );
  const [registerData, setRegisterData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    loadRegister();
  }, [selectedMonth]);

  const loadRegister = async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `get_attendance_register.php?month=${selectedMonth}`,
      );
      const employeesData = res.data.employees || [];
      const rawAttendance = res.data.attendance || {};

      // FIX: Ensure all multipliers are numbers so the <select> can match them
      const processedAttendance = {};
      Object.keys(rawAttendance).forEach((empId) => {
        processedAttendance[empId] = {};
        Object.keys(rawAttendance[empId]).forEach((date) => {
          const record = rawAttendance[empId][date];
          processedAttendance[empId][date] = {
            ...record,
            multiplier: parseFloat(record.multiplier) || 0, // Force numeric type
          };
        });
      });

      setEmployees(employeesData);
      setRegisterData(processedAttendance);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateCell = (empId, dateStr, payload) => {
    setRegisterData((prev) => ({
      ...prev,
      [empId]: {
        ...prev[empId],
        [dateStr]: { ...(prev[empId]?.[dateStr] || {}), ...payload },
      },
    }));
  };

  const toggleMonthly = (empId, dateStr) => {
    const current = registerData[empId]?.[dateStr] || {
      status: null,
      multiplier: 0,
    };
    let nextStatus, nextMult;
    if (!current.status) {
      nextStatus = "Present";
      nextMult = 1;
    } else if (current.status === "Present") {
      nextStatus = "Half-day";
      nextMult = 0.5;
    } else if (current.status === "Half-day") {
      nextStatus = "Absent";
      nextMult = 0;
    } else {
      nextStatus = null;
      nextMult = 0;
    }
    updateCell(empId, dateStr, { status: nextStatus, multiplier: nextMult });
  };

  const handleBulkSave = async () => {
    setSaving(true);
    try {
      const payload = [];

      // Iterate through each employee in the register state
      Object.keys(registerData).forEach((empId) => {
        // Find the employee object to get their current rate and default site
        const emp = employees.find((e) => String(e.id) === String(empId));

        // SAFETY CHECK: If employee is not found in the master list, skip this record
        if (!emp) {
          console.warn(
            `Employee ID ${empId} not found in master list. Skipping.`,
          );
          return;
        }

        Object.keys(registerData[empId]).forEach((date) => {
          const record = registerData[empId][date];

          // Only sync if there is actual data (status or time)
          const hasStatus = record.status && record.status !== "";
          const hasTime = record.in_time && record.in_time !== "00:00:00";

          if (hasStatus || hasTime) {
            payload.push({
              employee_id: empId,
              date: date,
              status: record.status || "Present",
              multiplier: parseFloat(record.multiplier) || 0,
              in_time: record.in_time || "00:00:00",
              out_time: record.out_time || "00:00:00",
              applied_rate: emp.rate, // Now safe to access
              project_id: record.project_id || emp.assigned_site_id || 0,
            });
          }
        });
      });

      if (payload.length === 0) {
        alert("No changes detected to sync.");
        setSaving(false);
        return;
      }

      await api.post("save_bulk_attendance_monthly1.php", {
        attendance: payload,
      });

      alert(`Success: ${payload.length} records synced!`);
      loadRegister(); // Refresh to ensure UI is in sync with DB
    } catch (err) {
      console.error(err);
      alert("Save failed. Check console for details.");
    } finally {
      setSaving(false);
    }
  };

  const daysCount = new Date(
    selectedMonth.split("-")[0],
    selectedMonth.split("-")[1],
    0,
  ).getDate();
  const daysArray = Array.from({ length: daysCount }, (_, i) => i + 1);

  const getStatusStyle = (status, isHourly) => {
    if (isHourly && status) return "bg-indigo-500 text-white";
    switch (status) {
      case "Present":
        return "bg-emerald-500 text-white";
      case "Absent":
        return "bg-rose-500 text-white";
      case "Half-day":
        return "bg-amber-500 text-white";
      default:
        return "bg-slate-50 text-slate-200";
    }
  };

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 italic uppercase tracking-tighter leading-none">
            Global Register
          </h1>
          <p className="text-slate-400 font-bold text-[10px] uppercase tracking-[0.3em] mt-1 italic">
            Multi-Pay Type Unified Console
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <input
            type="text"
            placeholder="Search staff..."
            className="p-2 px-4 bg-white border border-slate-200 rounded-xl font-bold text-xs outline-none focus:border-indigo-500 w-48"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <input
            type="month"
            className="p-2 bg-white border border-slate-200 rounded-xl font-black text-xs"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />
          <button
            onClick={handleBulkSave}
            disabled={saving}
            className="bg-slate-900 text-white px-6 py-2 rounded-xl font-black text-[10px] uppercase shadow-lg flex items-center gap-2 hover:bg-indigo-600 transition-all"
          >
            {saving ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Save size={14} />
            )}{" "}
            Sync All Changes
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-20 text-center font-black animate-pulse text-slate-300 italic uppercase tracking-widest">
          Compiling Attendance Matrix...
        </div>
      ) : (
        <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-900 text-white text-[9px] font-black uppercase">
                  <th className="sticky left-0 z-30 bg-slate-900 p-4 border-r border-slate-800 text-left min-w-[200px]">
                    Staff Detail
                  </th>
                  {daysArray.map((day) => (
                    <th
                      key={day}
                      className="p-2 text-center min-w-[50px] border-r border-slate-800"
                    >
                      {day}
                    </th>
                  ))}
                  <th className="sticky right-0 z-20 bg-indigo-600 p-4">
                    Summary
                  </th>
                </tr>
              </thead>
              <tbody>
                {employees
                  .filter((e) =>
                    e.name.toLowerCase().includes(searchTerm.toLowerCase()),
                  )
                  .map((emp) => {
                    let totalUnits = 0;

                    return (
                      <tr
                        key={emp.id}
                        className="hover:bg-slate-50/50 transition-all border-b border-slate-50"
                      >
                        <td className="sticky left-0 z-20 bg-white p-4 border-r border-slate-100 font-black text-slate-900 text-xs italic group-hover:bg-slate-50">
                          {emp.name}
                          <p className="text-[8px] text-indigo-500 font-bold not-italic uppercase tracking-tighter">
                            {emp.pay_type} Pay
                          </p>
                        </td>
                        {daysArray.map((day) => {
                          const dateStr = `${selectedMonth}-${day.toString().padStart(2, "0")}`;
                          const record = registerData[emp.id]?.[dateStr] || {
                            multiplier: 0,
                            status: null,
                            in_time: "",
                            out_time: "",
                          };
                          totalUnits += parseFloat(record.multiplier || 0);

                          return (
                            <td
                              key={day}
                              className="p-0 border-r border-slate-50 text-center"
                            >
                              {emp.pay_type === "Monthly" && (
                                <button
                                  onClick={() => toggleMonthly(emp.id, dateStr)}
                                  className={`w-full h-12 flex items-center justify-center transition-all ${getStatusStyle(record.status)}`}
                                >
                                  <span className="font-black text-[9px]">
                                    {record.status === "Present"
                                      ? "P"
                                      : record.status === "Half-day"
                                        ? "HD"
                                        : record.status === "Absent"
                                          ? "A"
                                          : "-"}
                                  </span>
                                </button>
                              )}

                              {emp.pay_type === "Daily" && (
                                <select
                                  className={`w-full h-12 text-[10px] font-black text-center outline-none appearance-none cursor-pointer transition-all ${
                                    Number(record.multiplier) > 0
                                      ? "bg-indigo-600 text-white"
                                      : "bg-slate-50 text-slate-300"
                                  }`}
                                  // FIX: Force Number comparison for the selected value
                                  value={Number(record.multiplier || 0)}
                                  onChange={(e) => {
                                    const m = parseFloat(e.target.value);
                                    updateCell(emp.id, dateStr, {
                                      multiplier: m,
                                      status: m > 0 ? "Present" : "Absent",
                                    });
                                  }}
                                >
                                  <option value="0" className="text-slate-900">
                                    ABSENT
                                  </option>
                                  {[0.5, 1, 1.2, 1.5, 2, 2.5].map((v) => (
                                    <option
                                      key={v}
                                      value={v}
                                      className="text-slate-900"
                                    >
                                      {v} SHIFT
                                    </option>
                                  ))}
                                </select>
                              )}

                              {emp.pay_type === "Hourly" && (
                                <div
                                  className={`w-full h-12 flex flex-col items-center justify-center p-1 gap-0.5 border-none ${record.in_time && record.in_time !== "00:00:00" ? "bg-indigo-500" : "bg-slate-50"}`}
                                >
                                  <input
                                    type="time"
                                    className="w-full bg-transparent text-[7px] font-black text-center outline-none text-white placeholder-slate-300"
                                    value={record.in_time || ""}
                                    onChange={(e) =>
                                      updateCell(emp.id, dateStr, {
                                        in_time: e.target.value,
                                      })
                                    }
                                  />
                                  <input
                                    type="time"
                                    className="w-full bg-transparent text-[7px] font-black text-center outline-none text-white placeholder-slate-300"
                                    value={record.out_time || ""}
                                    onChange={(e) =>
                                      updateCell(emp.id, dateStr, {
                                        out_time: e.target.value,
                                      })
                                    }
                                  />
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td className="sticky right-0 z-10 p-4 text-center font-black text-indigo-600 bg-indigo-50 text-xs border-l border-indigo-100">
                          {totalUnits.toFixed(1)}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveAttendanceRegister;
